/**
 * Progressive-disclosure section for the agent editor (§6): a bordered
 * section whose header is always visible; the body opens on demand.
 * Everything except Identity ships collapsed with sane defaults.
 */

import { useId, useState, type ReactNode } from "react";

import { cx } from "../lib/format";

export interface CollapsibleProps {
  title: string;
  /** One-line summary shown while collapsed — surface the defaults. */
  subtitle?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Right-aligned header slot, e.g. a Badge. */
  trailing?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Collapsible({
  title,
  subtitle,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  trailing,
  className,
  children,
}: CollapsibleProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const contentId = useId();

  function toggle() {
    const next = !open;
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  }

  return (
    <section className={cx("rounded border border-line bg-surface", className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-raised"
      >
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className={cx(
            "h-3.5 w-3.5 shrink-0 text-muted transition-transform",
            open && "rotate-90",
          )}
          fill="none"
        >
          <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-display text-base tracking-display text-bright">{title}</span>
          {subtitle && !open && (
            <span className="truncate text-xs text-muted">{subtitle}</span>
          )}
        </span>
        {trailing && <span className="shrink-0">{trailing}</span>}
      </button>
      <div
        id={contentId}
        className={cx(
          "grid transition-[grid-template-rows] duration-150 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-line px-4 py-4">{children}</div>
        </div>
      </div>
    </section>
  );
}
